<script setup>
import { reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, requiredIf } from '@vuelidate/validators';
import ChunkType from '../helpers/chunkType';

import ComboBox from 'next/combobox/ComboBox.vue';
import MultiselectParametersDropdown from './multiselect/MultiselectParametersDropdown.vue';

const props = defineProps({
  source: {
    type: Object,
    default: () => {},
  },
});

const emit = defineEmits(['submit']);

const { t } = useI18n();

const initialState = {
  type: ChunkType.TEXT,
  text: '',
  question: '',
  answer: '',
};

const parameters = Object.entries(props.source.parameters).map(
  ([key, value]) => ({
    name: key,
    value,
  })
);

const state = reactive({ ...initialState });
const textInput = ref(null);
const questionInput = ref(null);
const answerInput = ref(null);

const rules = {
  type: { required },
  text: {
    requiredIf: requiredIf(() => state.type === ChunkType.TEXT),
  },
  question: {
    requiredIf: requiredIf(() => state.type === ChunkType.QNA),
  },
  answer: {
    requiredIf: requiredIf(() => state.type === ChunkType.QNA),
  },
};

const v$ = useVuelidate(rules, state);

const resetState = () => {
  Object.assign(state, initialState);
};

const prepareFormData = () => {
  const sourceChunk = {
    chunk_type: state.type,
    status: 0,
  };

  if (state.type === ChunkType.QNA) {
    sourceChunk.question = state.question;
    sourceChunk.answer = state.answer;
  } else if (state.type === ChunkType.TEXT) {
    sourceChunk.text = state.text;
  }

  return sourceChunk;
};

const validate = async () => {
  const isFormValid = await v$.value.$validate();
  if (!isFormValid) return null;

  return prepareFormData();
};

const handleSubmit = async () => {
  const isFormValid = await validate();
  if (!isFormValid) return;

  emit('submit', prepareFormData());
};

const insertParameter = (model, parameter, inputRef) => {
  const inputEl = inputRef;
  if (!inputEl) return;

  const cursorPosition = inputEl.selectionStart;
  const formattedParameter = `%{${parameter.name.toUpperCase()}}`;
  const previousValue = model.$model;

  model.$model =
    previousValue.slice(0, cursorPosition) +
    formattedParameter +
    previousValue.slice(cursorPosition);

  // Set cursor position after the inserted parameter
  setTimeout(() => {
    inputEl.focus();
    inputEl.setSelectionRange(
      cursorPosition + formattedParameter.length,
      cursorPosition + formattedParameter.length
    );
  }, 0);
};

const chunkTypeList = [
  {
    value: ChunkType.TEXT,
    label: t(
      'WGPT_BOTS.EDIT.TABS.KNOWLEDGE_BASE.TABS.ITEMS.CHUNKS.CHUNK_TYPES.TEXT'
    ),
  },
  {
    value: ChunkType.QNA,
    label: t(
      'WGPT_BOTS.EDIT.TABS.KNOWLEDGE_BASE.TABS.ITEMS.CHUNKS.CHUNK_TYPES.Q_AND_A'
    ),
  },
];

defineExpose({ validate, resetState });
</script>

<template>
  <form class="flex flex-col gap-4 pb-4" @submit.prevent="handleSubmit">
    <label>
      {{
        $t(
          'WGPT_BOTS.EDIT.TABS.KNOWLEDGE_BASE.TABS.ITEMS.CHUNKS.CREATE.FORM.CHUNK_TYPE.LABEL'
        )
      }}
      <ComboBox
        v-model="state.type"
        :options="chunkTypeList"
        :has-error="v$.type.$error"
        class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
        @change="v$.type.$touch"
      />
    </label>

    <template v-if="state.type === ChunkType.TEXT">
      <div>
        <label :class="{ error: v$.text.$error }">
          {{
            $t(
              'WGPT_BOTS.EDIT.TABS.KNOWLEDGE_BASE.TABS.ITEMS.CHUNKS.CREATE.FORM.TEXT.LABEL'
            )
          }}
          <div class="flex relative">
            <textarea
              ref="textInput"
              v-model="state.text"
              placeholder="Aa..."
              @blur="v$.text.$touch"
            />

            <MultiselectParametersDropdown
              class="absolute ltr:right-0 rtl:left-0 top-0 my-2 mx-1"
              :options="parameters"
              @add="e => insertParameter(v$.text, e, textInput)"
            />
          </div>
        </label>
      </div>
    </template>

    <template v-else>
      <div>
        <label :class="{ error: v$.question.$error }">
          {{
            $t(
              'WGPT_BOTS.EDIT.TABS.KNOWLEDGE_BASE.TABS.ITEMS.CHUNKS.CREATE.FORM.QUESTION.LABEL'
            )
          }}
          <div class="flex relative">
            <textarea
              ref="textInput"
              v-model="state.question"
              placeholder="Aa..."
              @blur="v$.question.$touch"
            />

            <MultiselectParametersDropdown
              class="absolute ltr:right-0 rtl:left-0 top-0 my-2 mx-1"
              :options="parameters"
              @add="e => insertParameter(v$.question, e, questionInput)"
            />
          </div>
        </label>
      </div>

      <div>
        <label :class="{ error: v$.answer.$error }">
          {{
            $t(
              'WGPT_BOTS.EDIT.TABS.KNOWLEDGE_BASE.TABS.ITEMS.CHUNKS.CREATE.FORM.ANSWER.LABEL'
            )
          }}
          <div class="flex relative">
            <textarea
              ref="textInput"
              v-model="state.answer"
              placeholder="Aa..."
              @blur="v$.answer.$touch"
            />

            <MultiselectParametersDropdown
              class="absolute ltr:right-0 rtl:left-0 top-0 my-2 mx-1"
              :options="parameters"
              @add="e => insertParameter(v$.answer, e, answerInput)"
            />
          </div>
        </label>
      </div>
    </template>
  </form>
</template>

<style lang="scss" scoped>
textarea {
  @apply block w-full text-sm !px-3 !py-2.5 !mb-0 border rounded-lg !bg-n-alpha-black2 file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-n-slate-10 dark:placeholder:text-n-slate-10 disabled:cursor-not-allowed disabled:opacity-50 text-n-slate-12 transition-all duration-500 ease-in-out h-28 border-n-weak dark:border-n-weak hover:border-n-slate-6 dark:hover:border-n-slate-6 disabled:border-n-weak dark:disabled:border-n-weak focus:border-n-brand dark:focus:border-n-brand;
}
</style>
